import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug } from '../lib/cms';
import Gallery from '../components/Gallery';

export default function RentCamp() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadData() {
      const page = await fetchPageBySlug('lej-campen');
      setPageData(page);
    }
    loadData();
  }, []);

  const metaTitle = pageData?.meta_title?.da || "Lej hele pladsen til events, selskaber & bryllup - Campen Camping";
  const metaDesc = pageData?.meta_description?.da || "Hold dit næste arrangement, bryllup eller firmaevent hos Campen Camping. Plads til 100 spisende i klubhuset og 100 overnattende gæster.";

  return (
    <div className="py-12 bg-stone-50 min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Intro */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
            Eksklusiv leje
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#0f0d0e] mt-1 mb-4">
            Lej hele Campen til dit næste event
          </h1>
          <p className="text-stone-600 text-sm md:text-base leading-relaxed">
            Skab uforglemmelige minder på Samsø. Hos Campen Camping kan du leje hele pladsen til private fester, bryllupper, familietræf, virksomhedsarrangementer, kurser eller træf.
          </p>
        </div>

        {/* Key Stats Block */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white p-8 rounded-2xl border border-stone-200/50 shadow-sm text-center flex flex-col items-center">
            <div className="bg-[#67aedd]/10 text-[#67aedd] p-4 rounded-full text-2xl mb-4">
              <i className="fa-solid fa-utensils" />
            </div>
            <h3 className="font-serif font-bold text-[#0f0d0e] text-xl mb-1">100 Spisende</h3>
            <p className="text-stone-500 text-sm">Plads til ca. 100 spisende personer i det charmerende klubhus.</p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-stone-200/50 shadow-sm text-center flex flex-col items-center">
            <div className="bg-[#67aedd]/10 text-[#67aedd] p-4 rounded-full text-2xl mb-4">
              <i className="fa-solid fa-bed" />
            </div>
            <h3 className="font-serif font-bold text-[#0f0d0e] text-xl mb-1">100 Sovende</h3>
            <p className="text-stone-500 text-sm">Plads til ca. 100 sovende gæster i vores feriehuse, hytter, værelser og campingvogne.</p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-stone-200/50 shadow-sm text-center flex flex-col items-center">
            <div className="bg-[#67aedd]/10 text-[#67aedd] p-4 rounded-full text-2xl mb-4">
              <i className="fa-solid fa-water" />
            </div>
            <h3 className="font-serif font-bold text-[#0f0d0e] text-xl mb-1">Egen badebro</h3>
            <p className="text-stone-500 text-sm">Direkte adgang til havet, kun 300 meter til badebro og berømte solnedgange.</p>
          </div>
        </div>

        {/* Klubhuset Detail Section */}
        <div className="bg-white rounded-2xl border border-stone-200/60 shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16 p-8 md:p-12">
          <div className="flex flex-col justify-center">
            <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider mb-2">
              Fælleslokaler
            </span>
            <h2 className="text-3xl font-serif font-bold text-[#0f0d0e] mb-4">
              Det charmerende Klubhus
            </h2>
            <p className="text-stone-600 text-sm md:text-base leading-relaxed mb-6">
              Klubhuset er pladsens naturlige samlingspunkt. Det byder på en uformel og indbydende atmosfære med en fantastisk havudsigt, som er ideel til bespisning, workshops, foredrag og hyggeligt samvær. Huset rummer et fleksibelt opholdsrum, der nemt kan tilpasses både mødeopstilling, langborde eller caféopstilling.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-stone-600 mb-8">
              <li className="flex items-center gap-2.5">
                <i className="fa-solid fa-circle-check text-[#67aedd]" /> Stort anretterkøkken
              </li>
              <li className="flex items-center gap-2.5">
                <i className="fa-solid fa-circle-check text-[#67aedd]" /> Service til 100 personer
              </li>
              <li className="flex items-center gap-2.5">
                <i className="fa-solid fa-circle-check text-[#67aedd]" /> To store grill på terrassen
              </li>
              <li className="flex items-center gap-2.5">
                <i className="fa-solid fa-circle-check text-[#67aedd]" /> Fire toiletter (inkl. handicap)
              </li>
              <li className="flex items-center gap-2.5">
                <i className="fa-solid fa-circle-check text-[#67aedd]" /> Stor terrasse med havudsigt
              </li>
              <li className="flex items-center gap-2.5">
                <i className="fa-solid fa-circle-check text-[#67aedd]" /> Trådløst Wi-Fi
              </li>
            </ul>
            <div className="flex gap-4">
              <Link
                to="/kontakt"
                className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-6 py-3 rounded-full text-sm shadow-sm transition-colors"
              >
                Forespørg på leje
              </Link>
              <Link
                to="/feriehuse?valgt=klubhuset"
                className="bg-stone-100 hover:bg-stone-200 text-[#0f0d0e] font-semibold px-6 py-3 rounded-full text-sm transition-colors"
              >
                Se klubhus detaljer
              </Link>
            </div>
          </div>

          <div className="relative rounded-xl overflow-hidden aspect-4/3 shadow-sm bg-stone-100">
            <img
              src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/3.jpeg"
              alt="Klubhuset indvendigt"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Event Types Grid */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl font-serif font-bold text-[#0f0d0e]">
              Oplagt til mange typer arrangementer
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Vores fleksible rammer og smukke natur gør pladsen perfekt til både private og professionelle begivenheder.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-stone-200/50 shadow-sm">
              <h4 className="font-serif font-bold text-[#0f0d0e] text-lg mb-2">Bryllup & Fest</h4>
              <p className="text-stone-500 text-xs leading-relaxed">
                Hold et uformelt og romantisk ø-bryllup med overnatning til alle gæster og fest i klubhuset med solnedgang over vandet.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-stone-200/50 shadow-sm">
              <h4 className="font-serif font-bold text-[#0f0d0e] text-lg mb-2">Familietræf</h4>
              <p className="text-stone-500 text-xs leading-relaxed">
                Saml hele familien på tværs af generationer. Børnene kan lege på de store grønne arealer, mens de voksne hygger på terrassen.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-stone-200/50 shadow-sm">
              <h4 className="font-serif font-bold text-[#0f0d0e] text-lg mb-2">Firma & Kurser</h4>
              <p className="text-stone-500 text-xs leading-relaxed">
                Få ro til fordybelse, teambuilding og workshops i uforstyrrede rammer med frisk havluft og masser af plads.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-stone-200/50 shadow-sm">
              <h4 className="font-serif font-bold text-[#0f0d0e] text-lg mb-2">Træf & Grupper</h4>
              <p className="text-stone-500 text-xs leading-relaxed">
                Vi har stolte traditioner for at huse biltræf, motorcykeltræf, golfture og andre foreningsrejser med fællesskab i højsædet.
              </p>
            </div>
          </div>
        </div>

        {/* Gallery */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-[#67aedd] text-xs font-bold uppercase tracking-wider">
              Galleri
            </span>
            <h2 className="text-3xl font-serif font-bold text-[#0f0d0e] mt-1">
              Billeder fra selskaber og events
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Se de hyggelige rammer i klubhuset og de skønne udeområder.
            </p>
          </div>
          <Gallery slug="events-selskaber" />
        </div>

        {/* CTA Banner */}
        <div className="bg-gradient-to-b from-stone-50 to-[#e8f3fa] text-stone-800 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden shadow-sm border border-stone-200/60">
          <div className="absolute inset-0 z-0 opacity-5">
            <img
              src="https://assets.auxodev.dk/assets/d026a2b2-f90d-4c19-95d2-042cbc8d93d8/3.jpeg"
              alt="Campen Camping"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-4">
              Vil du høre mere om leje af hele pladsen?
            </h2>
            <p className="text-stone-600 text-sm md:text-base mb-8 leading-relaxed">
              Kontakt os for en uforpligtende snak om dit næste arrangement. Vi hjælper gerne med at skræddersy rammerne, så de passer præcis til dine ønsker.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/kontakt"
                className="bg-[#67aedd] hover:bg-[#5aa1cc] text-white font-bold px-8 py-3.5 rounded-full shadow-md transition-colors text-sm"
              >
                <i className="fa-solid fa-envelope mr-2" /> Kontakt os for tilbud
              </Link>
              <a
                href="tel:+4551540023"
                className="bg-white hover:bg-stone-50 text-stone-800 border border-stone-200 font-semibold px-8 py-3.5 rounded-full transition-colors text-sm shadow-sm"
              >
                <i className="fa-solid fa-phone mr-2" /> Ring på +45 5154 0023
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
